import { useState, FormEvent } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const FORM_URL = 'https://readdy.ai/api/form/d742jvv5hic0eqh31070';

const productOptions = [
  'MDF / MDP / HDF',
  'Plywood (ACx, BCx, CDx, HDO, MDO, PSF)',
  'OSB',
  'Pallet KD Lumber',
  'KD Fencing',
  'Treated Lumber & Plywood',
  'Multiple Products',
  'Other',
];

export default function ContactSection() {
  const { ref, isVisible } = useScrollAnimation();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (message.length > 500) return;
    
    setFormStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();

    Array.from(form.elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) {
        data.append(input.name, input.value);
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setFormStatus('success');
        form.reset();
        setMessage('');
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left — Info */}
          <div
            ref={ref}
            className={`transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#2D5E3F]/10 rounded-full mb-6">
              <span className="w-2 h-2 rounded-full bg-[#2D5E3F] inline-block"></span>
              <span className="text-[#2D5E3F] text-xs font-semibold tracking-widest uppercase">Get in Touch</span>
            </div>
            <h2 className="font-playfair text-3xl md:text-5xl text-[#1C1C1C] font-semibold leading-tight mb-6">
              Let&apos;s Build Something<br />
              <em className="italic font-normal text-[#2D5E3F]">Sustainable Together</em>
            </h2>
            <p className="text-stone-600 text-base leading-relaxed mb-10">
              Ready to explore how we can connect you with premium forest products from South America? 
              Fill out the form and our team will get back to you within 24 hours.
            </p>

            {/* Contact cards */}
            <div className="space-y-4">
              {[
                {
                  icon: 'ri-phone-line',
                  label: 'Call Us',
                  value: '305-339-7154',
                  sub: 'Mon–Fri, 9am–6pm',
                },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4 p-4 rounded-xl bg-[#F7F3EC]">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#2D5E3F]/10 flex-shrink-0">
                    <i className={`${item.icon} text-[#2D5E3F] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs text-stone-500 font-medium">{item.label}</div>
                    <div className="text-sm font-semibold text-[#1C1C1C]">{item.value}</div>
                    <div className="text-xs text-stone-400">{item.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Sustainability note */}
            <div className="mt-8 p-5 rounded-2xl bg-[#2D5E3F]/5 border border-[#2D5E3F]/15">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                  <i className="ri-leaf-line text-[#2D5E3F] text-xl"></i>
                </div>
                <p className="text-stone-600 text-sm leading-relaxed">
                  <strong className="text-[#1C1C1C]">Sustainability is our foundation.</strong> We only work with 
                  suppliers that meet our environmental and social responsibility standards.
                </p>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="bg-[#F7F3EC] rounded-2xl p-8 lg:p-10">
            {formStatus === 'success' ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#2D5E3F]/10 mx-auto mb-4">
                  <i className="ri-check-line text-[#2D5E3F] text-3xl"></i>
                </div>
                <h3 className="font-playfair text-xl font-semibold text-[#1C1C1C] mb-2">Message Sent!</h3>
                <p className="text-stone-500 text-sm">Thank you for reaching out. Our team will contact you within 24 hours.</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-6 px-6 py-2.5 bg-[#2D5E3F] text-white text-sm rounded-full cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <h3 className="font-playfair text-xl font-semibold text-[#1C1C1C] mb-6">Request a Quote</h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      className="w-full px-4 py-3 bg-white rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#2D5E3F] transition-colors"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-stone-600 mb-1.5">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      className="w-full px-4 py-3 bg-white rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#2D5E3F] transition-colors"
                      placeholder="Smith"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 bg-white rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#2D5E3F] transition-colors"
                    placeholder="john@company.com"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Company Name</label>
                  <input
                    type="text"
                    name="company"
                    className="w-full px-4 py-3 bg-white rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#2D5E3F] transition-colors"
                    placeholder="Your Company Inc."
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">Product of Interest *</label>
                  <select
                    name="product"
                    required
                    className="w-full px-4 py-3 bg-white rounded-xl border border-stone-200 text-sm text-stone-800 focus:outline-none focus:border-[#2D5E3F] transition-colors cursor-pointer"
                  >
                    <option value="">Select a product...</option>
                    {productOptions.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-stone-600 mb-1.5">
                    Message *
                    <span className={`ml-2 ${message.length > 500 ? 'text-red-500' : 'text-stone-400'}`}>
                      ({message.length}/500)
                    </span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="w-full px-4 py-3 bg-white rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-[#2D5E3F] transition-colors resize-none"
                    placeholder="Tell us about your project, volume requirements, or any specific needs..."
                  />
                  {message.length > 500 && (
                    <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                  )}
                </div>

                {formStatus === 'error' && (
                  <p className="text-red-500 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending' || message.length > 500}
                  className="w-full py-4 bg-[#2D5E3F] text-white text-sm font-medium rounded-xl hover:bg-[#1C3A2C] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {formStatus === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-send-plane-line"></i>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
